import { Usuario, Rol } from '../types/auth';
import { normalizeRoleName, getRoleDisplayName } from './roleHierarchy';

export const SIN_GEOGRAFIA = 'Sin geografía asignada';

export const displayNombreRol = (nombre: string): string => {
  const normalized = normalizeRoleName(nombre);
  return normalized ? getRoleDisplayName(normalized) : nombre;
};

export interface SubgrupoRol {
  readonly rol: Rol;
  readonly usuarios: Usuario[];
}

export interface GrupoProvincia {
  readonly provincia: string;
  readonly total: number;
  readonly subgrupos: SubgrupoRol[];
}

export const compararGeografia = (a: Usuario, b: Usuario): number => {
  const diocesisA = a.lumisial?.diocesis?.nombre ?? '';
  const diocesisB = b.lumisial?.diocesis?.nombre ?? '';
  if (diocesisA !== diocesisB) return diocesisA.localeCompare(diocesisB, 'es');
  const lumisialA = a.lumisial?.nombre ?? '';
  const lumisialB = b.lumisial?.nombre ?? '';
  if (lumisialA !== lumisialB) return lumisialA.localeCompare(lumisialB, 'es');
  return `${a.nombres} ${a.apellidos}`.localeCompare(`${b.nombres} ${b.apellidos}`, 'es');
};

const compararRol = (a: Rol, b: Rol): number => {
  if (a.nivel !== b.nivel) return a.nivel - b.nivel;
  const aEsAdmin = a.nombre.startsWith('admin');
  const bEsAdmin = b.nombre.startsWith('admin');
  if (aEsAdmin !== bEsAdmin) return aEsAdmin ? -1 : 1;
  return a.nombre.localeCompare(b.nombre, 'es');
};

export const agruparUsuarios = (usuarios: Usuario[]): GrupoProvincia[] => {
  const porProvincia = new Map<string, Usuario[]>();
  for (const u of usuarios) {
    const provincia = u.lumisial?.provincia_eclesiastica?.nombre ?? SIN_GEOGRAFIA;
    const lista = porProvincia.get(provincia) ?? [];
    lista.push(u);
    porProvincia.set(provincia, lista);
  }

  const grupos: GrupoProvincia[] = [];
  for (const [provincia, usuariosDeProvincia] of porProvincia.entries()) {
    const porRol = new Map<string, Usuario[]>();
    for (const u of usuariosDeProvincia) {
      const lista = porRol.get(u.rol_id) ?? [];
      lista.push(u);
      porRol.set(u.rol_id, lista);
    }

    const subgrupos: SubgrupoRol[] = [...porRol.values()]
      .map((lista) => ({
        rol: lista[0].rol,
        usuarios: [...lista].sort(compararGeografia),
      }))
      .sort((a, b) => compararRol(a.rol, b.rol));

    grupos.push({ provincia, total: usuariosDeProvincia.length, subgrupos });
  }

  return grupos.sort((a, b) => {
    if (a.provincia === SIN_GEOGRAFIA) return 1;
    if (b.provincia === SIN_GEOGRAFIA) return -1;
    return a.provincia.localeCompare(b.provincia, 'es');
  });
};
